import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { newsApi, type ArticleDetail } from "../../api/news";
import type { ApiError } from "../../api/client";
import { SafeHtml } from "../../components/SafeHtml";
import { intlLocale } from "../../lib/format";

export default function NewsDetailPage() {
  const { i18n, t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ArticleDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    void newsApi.bySlug(slug).then(setArticle).catch((e: ApiError) => setError(e.message));
  }, [slug]);

  const locale = i18n.resolvedLanguage ?? "en";
  const tr = article ? article.translations[locale] ?? article.translations.en : null;
  const fellbackToEn = article && !article.translations[locale] && !!article.translations.en;

  if (error) return <div style={errorBox}>{error}</div>;
  if (!article || !tr) return <p style={{ color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</p>;

  // The "news-wp" category is the auto-imported bucket — show it as plain "News"
  // so users never see the internal slug.
  const friendlyCategory = article.categorySlug === "news-wp" ? "News" : article.categorySlug;
  const isUrl = article.coverMinioKey != null && /^https?:\/\//i.test(article.coverMinioKey);

  return (
    <article className="news-article" style={page}>
      <nav style={crumbs} aria-label="Breadcrumb">
        <Link to="/news" style={crumbLink}>{t("nav.news", "News")}</Link>
        <span style={crumbSep}>/</span>
        <span style={crumbCurrent}>{friendlyCategory}</span>
      </nav>

      <header style={header}>
        <span style={chip}>{friendlyCategory}</span>
        <h1 style={title}>{tr.title}</h1>
        <div style={meta}>
          {article.publishedAt && (
            <time dateTime={article.publishedAt}>
              {new Date(article.publishedAt).toLocaleDateString(intlLocale(locale), { day: "numeric", month: "long", year: "numeric" })}
            </time>
          )}
        </div>
      </header>

      {isUrl && (
        <div style={heroWrap}>
          <img src={article.coverMinioKey!} alt="" style={heroImg} />
        </div>
      )}

      {fellbackToEn && (
        <div style={fallbackBanner}>
          {t("news.detail.fallback", "Translation not available in {{loc}} — showing English.", { loc: locale.toUpperCase() })}
        </div>
      )}

      {tr.bodyHtml ? (
        <SafeHtml html={tr.bodyHtml} className="news-body" />
      ) : (
        tr.excerpt && <p style={{ fontSize: 16, color: "var(--ink-2)" }}>{tr.excerpt}</p>
      )}

      <footer style={footer}>
        <Link to="/news" style={backLink}>← {t("news.detail.back", "Back to News")}</Link>
      </footer>
    </article>
  );
}

const page: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 20,
  maxWidth: 820,
  margin: "0 auto",
  paddingBottom: 32,
};
const crumbs: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-4)",
  display: "flex",
  alignItems: "center",
  gap: 8,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  fontWeight: 600,
};
const crumbLink: React.CSSProperties = { color: "var(--brand-ink)", textDecoration: "none" };
const crumbSep: React.CSSProperties = { color: "var(--ink-4)" };
const crumbCurrent: React.CSSProperties = { color: "var(--ink-3)" };
const header: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  paddingBottom: 4,
};
const chip: React.CSSProperties = {
  alignSelf: "flex-start",
  background: "var(--brand-primary-50)",
  color: "var(--brand-ink)",
  fontSize: 11,
  padding: "3px 10px",
  borderRadius: 999,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: "uppercase",
};
const title: React.CSSProperties = {
  margin: 0,
  fontSize: 36,
  color: "var(--ink)",
  lineHeight: 1.2,
  letterSpacing: -0.2,
};
const meta: React.CSSProperties = { color: "var(--ink-4)", fontSize: 13 };
const heroWrap: React.CSSProperties = {
  width: "100%",
  aspectRatio: "16 / 9",
  borderRadius: 12,
  overflow: "hidden",
  background: "var(--surface-2)",
  boxShadow: "var(--shadow-2)",
};
const heroImg: React.CSSProperties = { width: "100%", height: "100%", objectFit: "cover", display: "block" };
const footer: React.CSSProperties = { marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border)" };
const backLink: React.CSSProperties = { color: "var(--brand-ink)", textDecoration: "none", fontSize: 14, fontWeight: 600 };
const fallbackBanner: React.CSSProperties = { padding: 10, background: "var(--warn-bg)", color: "var(--warn)", borderRadius: 6, fontSize: 12 };
const errorBox: React.CSSProperties = { padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
